import { Card, Modal, Panel, RsInput } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form, FormControl } from '@/components/formik-rsuite';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { IconButton, Alert } from 'rsuite';
import { defaultExchangeInfo } from './constants';
import useTable from '@/hooks/useTable';
import { LEVEL_TYPE } from '@/constants';
import {
  fetchActList,
  fetchConvertTicket,
  fetchExchangeTicket,
  fetchQrcodeExchangeTicket,
  fetchUseTicketList,
} from '@/services/exchange';
import useLoading from '@/hooks/useLoading';
import _ from 'lodash';
import ExchangeableTicket from './components/ExchangeableTicket';
import SendTicketList from './components/SendTicketList';
import ExchangedOrExpiredTicket, { TICKET_TYPE } from './components/ExchangedOrExpiredTicket';
import useTicketList from '@/hooks/preload/useTicketList';
import useTicketActList from '@/hooks/preload/useTicketActList';
import { fetchServiceMemberInfo } from '@/services/site';
import * as Yup from 'yup';
import { SERVICE_STATUS } from '@/services/constants';
import useRouter from '@/hooks/useRouter';

const Exchange = () => {
  const { query } = useRouter();
  const { data: ticketList, loading: ticketListLoading } = useTicketList();
  const { data: ticketActList, loading: ticketListActLoading } = useTicketActList();

  const [currentType, setCurrentType] = useState(TICKET_TYPE.EXCHANGED);

  const { run: getMemberInfo, loading: memberInfoLoading } = useRequest(fetchServiceMemberInfo, {
    manual: true,
  });
  const {
    run: getConvertTicket,
    data: convertTicketData = {},
    loading: convertTicketLoading,
  } = useRequest(fetchConvertTicket, {
    manual: true,
  });
  const {
    run: getActList,
    data: actListData = {},
    loading: actListLoading,
  } = useRequest(fetchActList, {
    manual: true,
  });
  const {
    run: getUseTicketList,
    data: useTicketData = {},
    loading: useTicketLoading,
  } = useRequest(fetchUseTicketList, {
    manual: true,
  });
  const { run: exchangeTicket, loading: exchangeLoading } = useRequest(fetchExchangeTicket, {
    manual: true,
  });
  const { run: qrcodeExchange, loading: qrcodeExchangeLoading } = useRequest(
    fetchQrcodeExchangeTicket,
    {
      manual: true,
    }
  );

  const { onChange: onChangeConvert } = useTable(async (p) => {
    const { values } = formik;
    const params = transformParams(Object.assign({}, values, p));
    await getConvertTicket(params);
  });
  const { onChange: onChangeActList } = useTable(async (p) => {
    const { values } = formik;
    const params = transformParams(Object.assign({}, values, p));
    await getActList(params);
  });
  const {
    prevParams: useTicketPreParams,
    onSearch: onSearchUseTicket,
    onChange: onChangeUseTicket,
  } = useTable(async (p) => {
    const { values } = formik;
    const params = transformParams(Object.assign({}, values, { type: currentType }, p));
    await getUseTicketList(params);
  });

  const [currentMember, setCurrentMember] = useState(null);
  const defaultModal = {
    show: false,
    id: null,
    ticketId: '',
    remarkCode: '',
  };
  const [showModal, setShowModal] = useState(defaultModal);

  useEffect(() => {
    if (query?.memberid) {
      const { memberid } = query;
      getListWithMember({ member_id: memberid }, formik.values);
    }
  }, []);

  const transformParams = (values) => {
    const params = Object.assign({}, _.omit(values, ['time_range', 'phone', 'user_code']), {
      start_at:
        (values?.time_range && values?.time_range?.length === 2 && values?.time_range[0]) || '',
      end_at:
        (values?.time_range && values?.time_range?.length === 2 && values?.time_range[1]) || '',
    });
    return params;
  };

  const getListWithMember = (memberParams, values) => {
    getMemberInfo(memberParams).then((res) => {
      if (res?.status === SERVICE_STATUS.SUCCESS) {
        if (res?.data?.id) {
          if (res?.data?.level !== LEVEL_TYPE.NOT) {
            const params = transformParams(Object.assign({}, values, { member_id: res.data.id }));

            formik.setFieldValue('member_id', res.data.id);
            setCurrentMember(res.data);

            getConvertTicket(params);
            getActList(params);
            getUseTicketList(
              Object.assign({}, params, {
                type: currentType,
                ...(currentType === TICKET_TYPE.EXCHANGED && useTicketPreParams),
              })
            );
          } else {
            Alert.error('会员不存在');
            formik.setFieldValue('member_id', null);
            setCurrentMember(null);
          }
        } else {
          Alert.error('会员不存在');
          formik.setFieldValue('member_id', null);
          setCurrentMember(null);
        }
      }
    });
  };

  const handleSubmit = (values) => {
    if (values?.phone || values?.user_code) {
      getListWithMember(
        {
          phone: formik.values?.phone,
          user_code: formik.values?.user_code,
        },
        values
      );
    } else {
      formik.setFieldValue('member_id', null);
      setCurrentMember(null);
      const params = transformParams(Object.assign({}, values, { member_id: null }));
      getConvertTicket(params);
      getUseTicketList(
        transformParams(
          Object.assign({}, params, {
            type: currentType,
            ...(currentType === TICKET_TYPE.EXCHANGED && useTicketPreParams),
          })
        )
      );
    }
  };

  const formik = useFormik({
    initialValues: defaultExchangeInfo,
    enableReinitialize: true, // initialValues默认值受控
    onSubmit: handleSubmit,
  });
  const qrFormik = useFormik({
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string().ensure().required('请输入卡券号'),
    }),
    onSubmit: (values, actions) => {
      qrcodeExchange({ code: values.code, extra: showModal.remarkCode })
        .then((res) => {
          if (res?.status === SERVICE_STATUS.SUCCESS) {
            Alert.success('兑换成功');
            handleSubmit(formik.values);
          }
        })
        .catch((e) => {
          actions.setErrors(e.formError);
        });
      qrFormik.resetForm();
      setShowModal(defaultModal);
    },
  });
  const exchangedFormik = useFormik({
    initialValues: { shopping_village: null, time_range: [] },
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        gift_start_at: values?.time_range?.[0] || '',
        gift_end_at: values?.time_range?.[1] || '',
      });
      onSearchUseTicket(params);
    },
  });

  const isEmpty =
    !convertTicketData?.content_list && !actListData?.content_list && !useTicketData?.content_list;
  const preLoading = useLoading(ticketListLoading || ticketListActLoading);

  return (
    <>
      <Panel
        title="兑换查询"
        toolbar={
          <div className="flex justify-end">
            <IconButton
              className="flex items-center h-[40px]"
              onClick={() => setShowModal({ ...showModal, show: true })}
            >
              <div className="text-[#999999] mr-[6px]">扫卡券二维码直接兑换</div>
              <Image src="/resources/images/scanner.svg" />
            </IconButton>
          </div>
        }
        leftShadow
        loading={preLoading || memberInfoLoading || exchangeLoading || qrcodeExchangeLoading}
        style={{
          height: isEmpty ? 'calc(100vh - var(--headerHeight) - var(--navBarHeight))' : 'auto',
        }}
      >
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div style={{ display: 'flex', width: 'calc(100% - 40px)' }}>
                <Card.Input label="手机号" formKey="phone" />
                <Card.Input label="会员号" formKey="user_code" />
                <Card.DateRangePicker
                  className="flex-[2]"
                  label="有效时间"
                  formKey="time_range"
                  labelWeight={1}
                  contentWeight={3}
                />
              </div>
              <div className="flex items-center">
                <Card.Picker
                  label="卡券名/卡券活动"
                  formKey="coupons_id"
                  pickerData={ticketList}
                  searchable
                  labelWeight={1}
                  contentWeight={3}
                />
                <Card.Picker
                  label="活动名"
                  formKey="ticket_act_id"
                  pickerData={ticketActList}
                  searchable
                  labelWeight={1}
                  contentWeight={3}
                />
                <IconButton type="submit" className="flex items-center w-[30px] ml-[10px]">
                  <Image src="/resources/images/match.svg" />
                </IconButton>
              </div>
            </Card>
          </Form>
        </FormikProvider>
        {isEmpty && (
          <div className="flex-1 flex justify-center items-center text-[#C5C2C2]">
            搜索客户手机号/会员号，可查看该客户完整兑换内容
          </div>
        )}
      </Panel>
      {!isEmpty && (
        <>
          <ExchangeableTicket
            data={convertTicketData}
            loading={convertTicketLoading}
            onChange={onChangeConvert}
            onExchange={(id, ticketId) => setShowModal({ ...showModal, show: true, id, ticketId })}
          />
          <ExchangedOrExpiredTicket
            data={useTicketData}
            loading={useTicketLoading}
            onChange={onChangeUseTicket}
            type={currentType}
            onSwitch={(currentType) => {
              setCurrentType(currentType);

              const params = transformParams(
                Object.assign({}, formik.values, { type: currentType })
              );
              getUseTicketList(params);
            }}
            onReSearch={() => handleSubmit(formik.values)}
            exchangedFormik={exchangedFormik}
          />
          {currentMember?.id && (
            <SendTicketList
              data={actListData}
              loading={actListLoading}
              onChange={onChangeActList}
              onReSearch={() => handleSubmit(formik.values)}
            />
          )}
        </>
      )}
      <Modal
        show={showModal.show}
        isInput
        content={
          <>
            <div className="flex justify-center h-[30px] leading-[30px] mb-[20px]">
              <span>扫码兑换二维码：</span>
              {showModal.id ? (
                <span>{showModal.ticketId}</span>
              ) : (
                <FormikProvider value={qrFormik}>
                  <Form className="flex-1">
                    <FormControl name="code" className="h-[30px]" />
                  </Form>
                </FormikProvider>
              )}
            </div>
            <RsInput
              className="h-[30px]"
              placeholder="如有需要，请在此备注兑换的卡号"
              value={showModal.remarkCode}
              onChange={(remarkCode) => setShowModal({ ...showModal, remarkCode })}
            />
          </>
        }
        confirm="立即兑换"
        cancel="取消"
        onConfirm={() => {
          if (showModal?.id) {
            exchangeTicket({
              id: showModal.id,
              extra: showModal.remarkCode,
            }).then((res) => {
              if (res?.status === SERVICE_STATUS.SUCCESS) {
                Alert.success('兑换成功');
                handleSubmit(formik.values);
              }
            });
            setShowModal(defaultModal);
          } else {
            qrFormik.submitForm();
          }
        }}
        onCancel={() => setShowModal(defaultModal)}
      />
    </>
  );
};

export default Exchange;
